'use client'

import { Button, Image } from '@vinted/web-ui'

import { getFingerprint } from '@marketplace-web/session-management/audit-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  landingPageEvent,
  ElectronicsLandingPageEvent,
  ElectronicsIntroductionSectionModel,
} from '@marketplace-web/marketing-campaigns/marketing-landing-page-data'
import { ShowAtBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import useElectronicsLpVersion from '../../hooks/useElectronicsLpVersion'

type Props = { content: ElectronicsIntroductionSectionModel }

const IntroductionSection = ({
  content: { title, description, headline, ctaText, ctaLink, image, imageMobile },
}: Props) => {
  const { track } = useTracking()
  const lpVersion = useElectronicsLpVersion()

  const handleOnCtaClick = async () =>
    track(
      landingPageEvent({
        event:
          lpVersion === 'buy'
            ? ElectronicsLandingPageEvent.BuyIntroLinkClick
            : ElectronicsLandingPageEvent.SellIntroLinkClick,
        targetDetails: ctaLink,
        deviceId: await getFingerprint(),
      }),
    )

  const getSrcSet = (dto: { x1Url: string; x2Url?: string }) => {
    if (dto.x2Url) {
      return `${dto.x1Url} 1x, ${dto.x2Url} 2x`
    }

    return undefined
  }

  return (
    <section data-testid="introduction-section" className="electronics__introduction-section">
      <h3 className="electronics__introduction-headline">{headline}</h3>
      <h1 className="electronics__introduction-title">{title}</h1>
      <div
        className="electronics__introduction-description"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: description }}
      />
      <div className="electronics__introduction-cta">
        <Button
          styling="filled"
          text={ctaText}
          url={ctaLink}
          onClick={handleOnCtaClick}
          testId="electronics__introduction-cta"
        />
      </div>
      <div className="electronics__introduction-image">
        <ShowAtBreakpoint breakpoint="portables">
          <Image
            src={imageMobile.x1Url}
            scaling="cover"
            testId="introduction-section-image"
            srcset={getSrcSet(imageMobile)}
          />
        </ShowAtBreakpoint>
        <ShowAtBreakpoint breakpoint="desktops">
          <Image
            src={image.x1Url}
            scaling="cover"
            testId="introduction-section-image"
            srcset={getSrcSet(image)}
          />
        </ShowAtBreakpoint>
      </div>
    </section>
  )
}

export default IntroductionSection
